import baseApi from './api';

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface ClinicAdmin {
  id: string;
  user_id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  email: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    status: string;
    is_active: boolean;
  };
  status: 'pending' | 'active' | 'archived';
  user_status: 'invited' | 'active' | 'blocked';
  is_active: boolean;
  is_practitioner?: boolean;
  is_deleted: boolean;
  user_type: string;
  role: Role | null;
  created_at: string;
  updated_at: string;
}

export interface ClinicAdminsResponse {
  success: boolean;
  data: {
    admins: ClinicAdmin[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface ClinicAdminResponse {
  success: boolean;
  data: {
    admin: {
      id: string;
      status: string;
    };
  };
  message?: string;
}

export const clinicAdminService = {
  /**
   * Get all admins for a clinic
   */
  getClinicAdmins: async (
    clinicId: string,
    params?: PaginationParams
  ): Promise<ClinicAdminsResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/admins`, { params });
    return response.data;
  },

  /**
   * Create (invite) a clinic admin. Sends invitation email.
   */
  createClinicAdmin: async (
    clinicId: string,
    data: { first_name: string; last_name?: string; email: string }
  ): Promise<{ success: boolean; data: ClinicAdmin; message: string }> => {
    const response = await baseApi.post(`/clinic/${clinicId}/admins`, data);
    return response.data;
  },

  /**
   * Toggle admin status (active/archived)
   */
  toggleAdminStatus: async (
    clinicId: string,
    adminId: string
  ): Promise<ClinicAdminResponse> => {
    const response = await baseApi.patch(`/clinic/${clinicId}/admins/${adminId}/status`);
    return response.data;
  },

  /**
   * Make a clinic admin also a practitioner
   * Creates a new practitioner or links an existing one to the clinic
   */
  makeAdminPractitioner: async (
    clinicId: string,
    adminId: string,
    data: {
      npi_number: string;
      credentials?: string;
      license_number?: string;
      dea_number?: string;
      phone?: string;
      fax?: string;
      address_line_1?: string;
      address_line_2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      existing_practitioner_id?: string;
    }
  ): Promise<{
    success: boolean;
    data: {
      practitioner: {
        id: string;
        first_name: string;
        last_name: string;
        npi: string;
        status: string;
      };
      linked_existing: boolean;
    };
    message: string;
  }> => {
    const response = await baseApi.post(
      `/clinic/${clinicId}/admins/${adminId}/make-practitioner`,
      data
    );
    return response.data;
  },
};

